import { createWebHistory, createRouter } from 'vue-router';

import store from '@/store';
import routes from './routes';
import appConfig from '../../app.config';

const router = createRouter({
    history: createWebHistory(),
    routes,
    base: import.meta.env.BASE_URL,
    scrollBehavior(to, from, savedPosition) {
        if (savedPosition) {
            return savedPosition;
        } else {
            return { top: 0, left: 0 };
        }
    }
});

// Before each route evaluates...
router.beforeEach(async (routeTo, routeFrom, next) => {
    const loggedIn = localStorage.getItem('user');
    const isTokenExpired = store.getters['accessibility/isTokenExpired'];

    // Public routes that don't require authentication
    const publicRoutes = ['login', 'auth.register', 'auth.forgot-password', 'auth.reset-password', 'auth.redirect', 'auth.verify'];

    // Routes allowed without layout change enforcement
    const passthroughRoutes = ['call', 'hsm.approval-template', 'auth.redirect'];

    // 🔹 Case 1: Public routes or invalid token
    if ((!isTokenExpired && loggedIn) || publicRoutes.includes(routeTo.name)) {
        if (
            loggedIn &&
            ['login', 'workspace', 'auth.register', 'auth.forgot-password', 'auth.reset-password', 'auth.verify'].includes(routeTo.name)
        ) {
            store.dispatch('layout/changeLayoutType', { layoutType: 'horizontal' });
            return next({ name: 'auth.workspace' }); // ⬅ return is crucial
        }

        store.dispatch('layout/changeLayoutType', { layoutType: 'twocolumn' });
        return next(); // ⬅ safe, only called once
    }

    // 🔹 Case 2: Passthrough routes
    if (passthroughRoutes.includes(routeTo.name)) {
        return next(); // ⬅ safe
    }

    // 🔹 Case 3: Fallback → redirect to login
    return next({ name: 'login' }); // ⬅ safe
});

router.beforeResolve(async (routeTo, routeFrom, next) => {
    // Create a `beforeResolve` hook, which fires whenever
    // `beforeRouteEnter` and `beforeRouteUpdate` would. This
    // allows us to ensure data is fetched even when params change,
    // but the resolved route does not. We put it in `meta` to
    // indicate that it's a hook we created, rather than part of
    // Vue Router (yet?).
    try {
        // For each matched route...
        for (const route of routeTo.matched) {
            await new Promise((resolve, reject) => {
                // If a `beforeResolve` hook is defined, call it with
                // the same arguments as the `beforeEnter` hook.
                if (route.meta && route.meta.beforeResolve) {
                    route.meta.beforeResolve(routeTo, routeFrom, (...args) => {
                        // If the user chose to redirect...
                        if (args.length) {
                            // If redirecting to the same route we're coming from...
                            // Complete the redirect.
                            next(...args);
                            reject(new Error('Redirected'));
                        } else {
                            resolve();
                        }
                    });
                } else {
                    // Otherwise, continue resolving the route.
                    resolve();
                }
            });
        }
        // If a `beforeResolve` hook chose to redirect, just return.
    } catch (error) {
        return;
    }
    document.title = routeTo.meta.title + ' | ' + appConfig.title;
    // If we reach this point, continue resolving the route.
    next();
});

export default router;
